// Developer: Ankit Gupta
/** Small time helpers for the chat: "2 min ago", "14:05", "done in 3.2 min". */
import i18n from '@/i18n';
import { browserOrUiLocale } from '@/utils/format';

// Called while a component renders, so the words follow the language of that render.
const words = (key: string, options?: Record<string, unknown>) => i18n.t(`chat:time.${key}`, options);

export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return words('justNow');
  const m = Math.round(s / 60);
  if (m < 60) return words('minAgo', { n: m });
  const h = Math.round(m / 60);
  if (h < 24) return words('hAgo', { n: h });
  const d = Math.round(h / 24);
  if (d < 7) return words('dAgo', { n: d });
  return new Date(iso).toLocaleDateString(browserOrUiLocale());
}

export function clock(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(browserOrUiLocale(), { hour: '2-digit', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString(browserOrUiLocale(), { day: 'numeric', month: 'short' })} ${time}`;
}

export function took(seconds: number | null | undefined): string {
  if (seconds == null) return '';
  if (seconds < 60) return words('doneSec', { n: seconds });
  return words('doneMin', { n: (seconds / 60).toFixed(1) });
}

export function fileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function countdown(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const left = Math.round((new Date(iso).getTime() - now) / 1000);
  if (left <= 0) return words('expiring');
  if (left >= 3600) return words('leftHM', { h: Math.floor(left / 3600), m: Math.floor((left % 3600) / 60) });
  const m = Math.floor(left / 60);
  const s = left % 60;
  return words('leftClock', { clock: `${m}:${String(s).padStart(2, '0')}` });
}

/** The estimate of the time a request still needs, in words; never a false precision. */
export function timeLeft(seconds: number): string {
  if (seconds < 20) return words('fewSeconds');
  if (seconds < 90) return words('aboutMinute');
  if (seconds < 3600) return words('aboutMin', { m: Math.round(seconds / 60) });
  return words('aboutHM', { h: Math.floor(seconds / 3600), m: Math.round((seconds % 3600) / 60) });
}
